import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import GoBackButton from "@/common/components/GoBackButton";
import { useDocumentOptions } from "@/common/hooks/queries/useDocumentOptions";
import { DirtySubmitHandler, useCustomForm } from "@/common/hooks/useCustomForm";
import { useGoBack } from "@/common/hooks/useGoBack";
import { FormTransformers } from "@/common/utils/formTransformers";
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist";
import { ContactDocumentsSection } from "@/features/privateCompanyLeadsFunnel/components/ContactDocumentsSection";
import { ContactPersonalInformationSection } from "@/features/privateCompanyLeadsFunnel/components/ContactPersonalInformationSection";
import { stateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant";
import useAdvancePrivateCompanyLeadContact from "@/features/privateCompanyLeadsFunnel/hooks/useAdvancePrivateCompanyLeadContact";
import useDeletePrivateCompanyLeadContact from "@/features/privateCompanyLeadsFunnel/hooks/useDeletePrivateCompanyLeadContact";
import useRegressContact from "@/features/privateCompanyLeadsFunnel/hooks/useRegressContact";
import useReviewContact from "@/features/privateCompanyLeadsFunnel/hooks/useReviewContact";
import useReviewContactDocument from "@/features/privateCompanyLeadsFunnel/hooks/useReviewContactDocument";
import useToggleContactDocument from "@/features/privateCompanyLeadsFunnel/hooks/useToggleContactDocument";
import useUpdatePrivateCompanyLeadContact from "@/features/privateCompanyLeadsFunnel/hooks/useUpdatePrivateCompanyLeadContact";
import { PrivateCompanyLeadsFunnelUpdateContactValidationSchema, updateContactSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema";
import { ContactData, ContactState } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead";
import { leadContactDocumentsDefaultValues } from "@/features/publicCompanyLeadsFunnel/services/leadContactDocumentDefaultValues";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { ChevronsRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const Form = ({
  contact,
  privateCompanyLeadState,
  privateCompanyLeadName
}: {
  contact?: ContactData,
  privateCompanyLeadState: keyof typeof stateStrategy,
  privateCompanyRestContacts: ContactData[],
  privateCompanyLeadName: string
}) => {
  const { privateCompanyLeadId } = useParams();

  const goBack = useGoBack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isInRegistered = useMemo(() => privateCompanyLeadState === 'registered', [privateCompanyLeadState]);
  const isInQualifying = useMemo(() => privateCompanyLeadState === 'qualifying', [privateCompanyLeadState]);

  const isInQualifyingManagementReview = useMemo(
    () => privateCompanyLeadState === 'qualifying_management_review',
    [privateCompanyLeadState]
  );

  const passedEveryRequirement = useMemo(
    () => contact?.requirement_list_summary?.steps?.every((step) => step.passed),
    [contact]
  );

  const hasDisapprovedReview = useMemo(
    () => contact?.last_review?.state === 'disapproved' || contact?.documents?.some((doc) => doc.last_review?.state === 'disapproved'),
    [contact]
  );

  const [editContact, setEditContact] = useState(false);
  const [documentToModal, setDocumentToModal] = useState('');
  const [files, setFiles] = useState<{ id: string, file: Blob, fileName: string }[]>([]);
  const [serverError, setServerError] = useState(false);

  const { data: documentOptions, isFetched: isDocumentOptionsFetched } = useDocumentOptions();

  const updateLeadOnSuccess = useCallback((response: AxiosResponse) => {
    editContact && setEditContact(false);
    setServerError(false);
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] });

    const contactDocuments = (response.data.data.contacts as ContactData[]).find(({ id }) => id === (contact?.id || ''))?.documents;

    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contactDocuments) as any);
  }, [editContact, setEditContact, setServerError, queryClient, privateCompanyLeadId, isDocumentOptionsFetched])

  const onToggleDocumentSuccess = (response: AxiosResponse) => {
    setServerError(false);
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] });

    const contactDocuments = (response.data.data.contacts as ContactData[]).find(({ id }) => id === (contact?.id || ''))?.documents;

    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contactDocuments) as any);
  }

  const redirectOnSuccess = useCallback(() => {
    goBack({ fallback: `/private-clients/${privateCompanyLeadId}/edit`, searchLastRouteFromFallback: true })
  }, [queryClient, navigate, privateCompanyLeadId])

  const onError = useCallback(() => setServerError(true), [])

  const {
    mutate: toggleDocument
  } = useToggleContactDocument({
    contactId: contact?.id,
    privateCompanyLeadId,
    onSuccess: onToggleDocumentSuccess,
    onError
  });

  const {
    mutate: deletePrivateCompanyLeadContact
  } = useDeletePrivateCompanyLeadContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
    onError
  });

  const {
    mutate: regressContact
  } = useRegressContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
    onError
  });

  const {
    mutate: advancePrivateCompanyLeadContact
  } = useAdvancePrivateCompanyLeadContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
    onError
  });

  const {
    mutate: approveContact
  } = useReviewContact({
    contactId: contact?.id,
    privateCompanyLeadId,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
    onError
  });

  const {
    mutate: declineContact
  } = useReviewContact({
    contactId: contact?.id,
    privateCompanyLeadId,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
    onError
  });

  const {
    mutate: approveContactDocument
  } = useReviewContactDocument({
    contactId: contact?.id,
    privateCompanyLeadId,
    documentId: documentToModal,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
    onError
  });

  const {
    mutate: declineContactDocument
  } = useReviewContactDocument({
    contactId: contact?.id,
    privateCompanyLeadId,
    documentId: documentToModal,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
    onError
  });

  const {
    register,
    handleDirtySubmit,
    control,
    setError,
    watch,
    setValue,
    resetDirtyFields,
    formState: { errors, isAnyDirty },
  } = useCustomForm<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>({
    resolver: zodResolver(updateContactSchema),
    defaultValues: {
      contacts: {
        full_name: contact?.full_name,
        email: contact?.email,
        role_in_company: contact?.role_in_company,
        phone: {
          number: contact?.phone?.number,
        },
        cpf: contact?.cpf,
        responsible_for_company_information: contact?.responsible_for_company_information,
      }
    },
    skipNotDirtyFieldsOnSubmit: {
      contacts: {
        documents: [
          {
            id: true,
            checked: true,
          },
        ],
      },
    },
  })

  const {
    mutate: updateContact,
    isPending: updateContactIsPending,
  } = useUpdatePrivateCompanyLeadContact({
    privateCompanyLeadId,
    contactId: contact?.id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>({ setError }),
  });

  const handleCancelDeletePress = () => {
    if (contact) {
      if (editContact) {
        setFiles([])
        setEditContact(false)
        resetDirtyFields({
          contacts: {
            full_name: contact?.full_name,
            email: contact?.email,
            role_in_company: contact?.role_in_company,
            phone: {
              number: contact?.phone?.number,
            },
            cpf: contact?.cpf,
            responsible_for_company_information: contact?.responsible_for_company_information,
            documents: leadContactDocumentsDefaultValues(documentOptions, contact.documents),
          }
        });
      } else {
        deletePrivateCompanyLeadContact()
      }
    }
  }

  const onSubmit: DirtySubmitHandler<PrivateCompanyLeadsFunnelUpdateContactValidationSchema> = (data) => {
    contact?.documents?.forEach((document, index) => {
      if (document.id && data.contacts?.documents[index]?.id === document.id) {
        data.contacts.documents[index]!.id = document.id;
        const file = files.find((file) => file.id === document.id)

        if (file) {
          data.contacts.documents[index]!.file = files.find((file) => file.id === document.id)?.file;
        }
      }
    })

    updateContact(data)
  };

  const submit = handleDirtySubmit(onSubmit);

  useEffect(() => {
    if (isDocumentOptionsFetched) {
      setValue(
        'contacts.documents',
        leadContactDocumentsDefaultValues(documentOptions, contact?.documents) as any,
        { shouldDirty: false }
      );
    }
  }, [isDocumentOptionsFetched, contact?.documents?.[0]?.file?.id]);

  return (
    <form>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback={`/private-clients/${privateCompanyLeadId}/edit`} />
          <p className="text-white-400 font-normal">{privateCompanyLeadName}</p>
          <ChevronsRight color='#ffffffb3' />
          <h2>{contact?.full_name || 'Novo contato'}</h2>
        </div>

        <div className="flex gap-4">
          {(contact?.state === ContactState.pending && !isInRegistered) && (
            <>
              <Button
                type="button"
                variant='outline-error'
                onClick={handleCancelDeletePress}
                disabled={updateContactIsPending}
              >
                {editContact ? 'Cancelar' : 'Deletar'}
              </Button>

              {!editContact ? (
                <Button
                  type="button"
                  onClick={() => setEditContact(true)}
                  name="edit"
                >
                  Editar
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    if (isAnyDirty) {
                      submit();
                    } else {
                      handleCancelDeletePress();
                    }
                  }}
                  isLoading={updateContactIsPending}
                  name="confirm"
                >
                  Confirmar
                </Button>
              )}
            </>
          )}

          {contact?.state === ContactState.pending && (
            <Button
              type="button"
              disabled={!passedEveryRequirement || updateContactIsPending || editContact}
              variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
              onClick={() => advancePrivateCompanyLeadContact()}
              name="advance_lead_contact"
            >
              Enviar solicitação
            </Button>
          )}

          {(contact?.state === ContactState.in_review && (isInQualifying || isInQualifyingManagementReview)) && (
            <>
              <Button
                type="button"
                disabled={!hasDisapprovedReview}
                variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                onClick={() => regressContact()}
              >
                Solicitar revisão
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-6 w-full">
        <Box className="flex w-full flex-col gap-4 h-[calc(100vh-162px)] overflow-y-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError && (
            <p className="text-sm text-danger-soft">
              Ocorreu um erro no servidor, cheque os campos e tente novamente
            </p>
          )}

          <ContactPersonalInformationSection
            privateCompanyLeadId={privateCompanyLeadId}
            contact={contact}
            register={register}
            control={control}
            errors={errors}
            showReviewButtons={contact?.state === ContactState.in_review && (isInQualifying || isInQualifyingManagementReview)}
            isPending={updateContactIsPending}
            approveContact={approveContact}
            declineContact={declineContact}
            disableFields={!editContact || updateContactIsPending}
          />

          <ContactDocumentsSection
            contact={contact}
            control={control}
            watch={watch}
            errors={errors}
            files={files}
            setFiles={setFiles}
            setDocumentToModal={setDocumentToModal}
            showReviewButtons={contact?.state === ContactState.in_review && (isInQualifying || isInQualifyingManagementReview)}
            approveContactDocument={approveContactDocument}
            declineContactDocument={declineContactDocument}
            toggleDocument={toggleDocument}
            editContact={editContact}
            isInRegistered={isInRegistered}
            updateContactIsPending={updateContactIsPending}
          />
        </Box>

        <Checklist
          requirement_list_summary={(isInQualifying || isInQualifyingManagementReview) ? contact?.requirement_list_summary : undefined}
          className="flex flex-col gap-2 justify-between h-full max-h-[calc(100vh-170px)]"
          Buttons={() => (
            <div className="flex flex-col w-full gap-2 mt-auto mb-[72px]">
              {contact?.state === ContactState.pending && (
                <Button
                  type="button"
                  disabled={!passedEveryRequirement || updateContactIsPending || editContact}
                  variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                  onClick={() => advancePrivateCompanyLeadContact()}
                >
                  Enviar solicitação
                </Button>
              )}

              {contact?.state === ContactState.awaiting_client_documents && (
                <Button
                  type="button"
                  disabled
                  variant="outline-disabled"
                >
                  Aguardando
                </Button>
              )}

              {(contact?.state === ContactState.in_review && (isInQualifying || isInQualifyingManagementReview)) && (
                <>
                  <Button
                    type="button"
                    disabled={!hasDisapprovedReview}
                    variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                    onClick={() => regressContact()}
                  >
                    Solicitar revisão
                  </Button>
                </>
              )}
            </div>
          )}
        />
      </div>
    </form>
  )
}

export default Form
